import { useEffect, useRef, useState } from "react";
import { BellRing, Volume2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

function selectFemaleVoice() {
  const voices = window.speechSynthesis?.getVoices?.() || [];
  const preferredNames = [
    "Samantha", "Victoria", "Karen", "Moira", "Tessa", "Fiona", "Ava",
    "Susan", "Zira", "Aria", "Jenny", "Sonia", "Google US English",
  ];
  return preferredNames.map((name) => voices.find((voice) => voice.name.includes(name))).find(Boolean)
    || voices.find((voice) => /^en(-|_)/i.test(voice.lang) && !/male/i.test(voice.name))
    || voices.find((voice) => /^en(-|_)/i.test(voice.lang))
    || voices[0];
}

function speakNewVisitor() {
  if (!("speechSynthesis" in window)) return false;
  try {
    let played = false;
    const play = () => {
      if (played) return;
      played = true;
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance("New Visitor");
      const femaleVoice = selectFemaleVoice();
      if (femaleVoice) utterance.voice = femaleVoice;
      utterance.rate = 1;
      utterance.pitch = 1.08;
      window.speechSynthesis.speak(utterance);
    };
    if (window.speechSynthesis.getVoices().length) play();
    else {
      const onVoices = () => { window.speechSynthesis.removeEventListener?.("voiceschanged", onVoices); play(); };
      window.speechSynthesis.addEventListener?.("voiceschanged", onVoices, { once: true });
      window.setTimeout(play, 500);
    }
    return true;
  } catch {
    return false;
  }
}

export function VoiceAlertControl({ events, siteId }) {
  const [enabled, setEnabled] = useState(true);
  const [armed, setArmed] = useState(false);
  const seen = useRef(new Set());
  const initialized = useRef(false);

  useEffect(() => {
    seen.current = new Set();
    initialized.current = false;
  }, [siteId]);

  useEffect(() => {
    if (!enabled || armed) return undefined;
    const arm = () => {
      if ("speechSynthesis" in window) window.speechSynthesis.resume();
      setArmed(true);
    };
    window.addEventListener("pointerdown", arm, { once: true });
    window.addEventListener("keydown", arm, { once: true });
    return () => {
      window.removeEventListener("pointerdown", arm);
      window.removeEventListener("keydown", arm);
    };
  }, [enabled, armed]);

  useEffect(() => {
    const visitorEvents = events.filter((event) => event.is_new_visitor);
    if (!initialized.current) {
      visitorEvents.forEach((event) => seen.current.add(event.event_id));
      initialized.current = true;
      return;
    }
    const arrivals = visitorEvents.filter((event) => !seen.current.has(event.event_id));
    arrivals.forEach((event) => seen.current.add(event.event_id));
    if (enabled && armed && arrivals.length) speakNewVisitor();
  }, [events, enabled, armed]);

  const testVoice = () => {
    setArmed(true);
    if (speakNewVisitor()) toast.success("Voice alert is working");
    else toast.error("Voice alerts are not supported by this browser");
  };

  return <section className={`voice-control ${enabled ? "voice-on" : ""}`} data-testid="voice-alert-control">
    <div className="voice-symbol"><BellRing size={22}/></div>
    <div className="voice-copy"><span>Female arrival voice</span><h2>Say “New Visitor”</h2><p data-testid="voice-alert-status">{enabled ? (armed ? "Listening for first-time visitors" : "Ready after your first click") : "Voice alerts are paused"}</p></div>
    <div className="voice-actions"><Button variant="outline" size="sm" onClick={testVoice} data-testid="test-voice-alert-button"><Volume2 size={16}/> Test voice</Button><Switch checked={enabled} onCheckedChange={setEnabled} data-testid="voice-alert-toggle" aria-label="Toggle new visitor voice alerts"/></div>
  </section>;
}
